// src/admin/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";

// ---------------- helpers ----------------
function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS <= bE && bS <= aE;
}

function ymd(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDaysISO(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return ymd(d);
}

function toneForBookingStatus(status) {
  const s = normalize(status);
  if (s === "approved") return "success";
  if (s === "requested") return "warning";
  if (s === "active") return "info";
  if (s === "completed") return "neutral";
  if (s === "cancelled") return "neutral";
  return "neutral";
}



export default function Dashboard() {
  const nav = useNavigate();

  const today = useMemo(() => ymd(new Date()), []);
  const next7 = useMemo(() => addDaysISO(today, 7), [today]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [memberships, setMemberships] = useState([]); // memberships + joined crm_contacts
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [approvedActiveBookings, setApprovedActiveBookings] = useState([]);

  // dashboard search
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr(null);

      try {
        const [
          { data: memData, error: memErr },
          { data: vehData, error: vehErr },
          { data: bAll, error: bAllErr },
          { data: bAA, error: bAAErr },
        ] = await Promise.all([
          supabase
            .from("memberships")
            .select(
              `
              id,
              created_at,
              status,
              tier,
              billing,
              days_remaining,
              can_book,
              age_verified,
              start_date,
              renewal_date,
              contact_id,
              profile_id,
              crm_contacts:contact_id (
                id,
                first_name,
                last_name,
                email,
                phone,
                stage,
                created_at
              )
            `
            )
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("vehicles")
            .select("id, name, category, status, location, tier_access, year, make, model, sub_category")
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("bookings")
            .select(
              "id, created_at, membership_id, vehicle_id, start_date, end_date, status, pickup_location, dropoff_location, concierge, notes"
            )
            .order("created_at", { ascending: false })
            .limit(700),

          supabase
            .from("bookings")
            .select("id, vehicle_id, start_date, end_date, status, created_at")
            .in("status", ["approved", "active"])
            .order("created_at", { ascending: false })
            .limit(1200),
        ]);

        if (memErr) throw memErr;
        if (vehErr) throw vehErr;
        if (bAllErr) throw bAllErr;
        if (bAAErr) throw bAAErr;

        if (!cancelled) {
          setMemberships(memData || []);
          setVehicles(vehData || []);
          setBookings(bAll || []);
          setApprovedActiveBookings(bAA || []);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------- indexes --------
  const membershipById = useMemo(() => {
    const m = new Map();
    for (const row of memberships) m.set(row.id, row);
    return m;
  }, [memberships]);

  const vehicleById = useMemo(() => {
    const m = new Map();
    for (const v of vehicles) m.set(v.id, v);
    return m;
  }, [vehicles]);

  // -------- KPIs --------
  const stats = useMemo(() => {
    const activeMembers = memberships.filter((m) => normalize(m.status) === "active").length;
    const prospects = memberships.filter((m) => normalize(m.status) === "prospect").length;

    const vehiclesAvailable = vehicles.filter((v) => normalize(v.status) === "available").length;
    const vehiclesOpsHold = vehicles.filter((v) => normalize(v.status) !== "available").length;

    const requested = bookings.filter((b) => normalize(b.status) === "requested").length;
    const approved = bookings.filter((b) => normalize(b.status) === "approved").length;
    const active = bookings.filter((b) => normalize(b.status) === "active").length;

    return { activeMembers, prospects, vehiclesAvailable, vehiclesOpsHold, requested, approved, active };
  }, [memberships, vehicles, bookings]);

  // -------- conflict detection for requested bookings --------
  const requestedWithConflict = useMemo(() => {
    const requested = bookings.filter((b) => normalize(b.status) === "requested").slice(0, 350);

    const byVehicle = new Map();
    for (const b of approvedActiveBookings) {
      if (!byVehicle.has(b.vehicle_id)) byVehicle.set(b.vehicle_id, []);
      byVehicle.get(b.vehicle_id).push(b);
    }

    return requested.map((b) => {
      const list = byVehicle.get(b.vehicle_id) || [];
      const conflict = list.some((x) => overlaps(b.start_date, b.end_date, x.start_date, x.end_date));
      return { ...b, __conflict: conflict };
    });
  }, [bookings, approvedActiveBookings]);

  const conflictsCount = useMemo(() => {
    return requestedWithConflict.filter((b) => b.__conflict).length;
  }, [requestedWithConflict]);

  // -------- concierge views --------
  const todaysPickups = useMemo(() => {
    const list = bookings
      .filter((b) => ["approved", "active"].includes(normalize(b.status)))
      .filter((b) => b.start_date === today)
      .slice(0, 20)
      .map((b) => enrichBooking(b, membershipById, vehicleById));
    return list;
  }, [bookings, today, membershipById, vehicleById]);

  const todaysDropoffs = useMemo(() => {
    const list = bookings
      .filter((b) => ["approved", "active"].includes(normalize(b.status)))
      .filter((b) => b.end_date === today)
      .slice(0, 20)
      .map((b) => enrichBooking(b, membershipById, vehicleById));
    return list;
  }, [bookings, today, membershipById, vehicleById]);

  const goingOutSoon = useMemo(() => {
    // approved bookings starting in next 7 days (incl today)
    const list = bookings
      .filter((b) => normalize(b.status) === "approved")
      .filter((b) => b.start_date >= today && b.start_date <= next7)
      .slice(0, 20)
      .map((b) => enrichBooking(b, membershipById, vehicleById));
    return list;
  }, [bookings, today, next7, membershipById, vehicleById]);

  const lowDays = useMemo(() => {
    // active members with low days remaining
    const list = memberships
      .filter((m) => normalize(m.status) === "active")
      .filter((m) => typeof m.days_remaining === "number" && m.days_remaining <= 2)
      .slice(0, 20)
      .map((m) => ({
        id: m.id,
        name: m.crm_contacts ? fullNameFromContact(m.crm_contacts) : "Member",
        email: m.crm_contacts?.email || "—",
        phone: m.crm_contacts?.phone || "—",
        tier: m.tier,
        days: m.days_remaining,
        can_book: !!m.can_book,
      }));
    return list;
  }, [memberships]);

  // -------- work queue --------
  const workQueue = useMemo(() => {
    const items = [];

    // booking requests (conflicts first)
    const requestsSorted = [...requestedWithConflict].sort((a, b) => {
      const ac = a.__conflict ? 1 : 0;
      const bc = b.__conflict ? 1 : 0;
      if (ac !== bc) return bc - ac; // conflicts first
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });

    for (const b of requestsSorted.slice(0, 8)) {
      const eb = enrichBooking(b, membershipById, vehicleById);
      items.push({
        key: `req_${b.id}`,
        label: b.__conflict ? "Resolve booking conflict" : "Review booking request",
        sub: `${eb.start_date} → ${eb.end_date} • ${eb.vehicleName} • ${eb.memberName}`,
        tone: b.__conflict ? "danger" : "warning",
        tag: b.__conflict ? "Conflict" : "Requested",
        onClick: () => nav(`/admin/bookings`),
      });
    }

    // ops hold vehicles
    for (const v of vehicles.filter((x) => normalize(x.status) !== "available").slice(0, 6)) {
      items.push({
        key: `veh_${v.id}`,
        label: "Resolve vehicle status",
        sub: `${v.name} • ${v.category}${v.sub_category ? ` • ${v.sub_category}` : ""}`,
        tone: "info",
        tag: String(v.status || ""),
        onClick: () => nav(`/admin/vehicles/${v.id}`),
      });
    }

    // booking blocked active members
    for (const m of memberships
      .filter((x) => normalize(x.status) === "active" && !x.can_book)
      .slice(0, 6)) {
      const c = m.crm_contacts || null;
      items.push({
        key: `m_${m.id}`,
        label: "Member active but booking disabled",
        sub: `${c ? fullNameFromContact(c) : "Member"} • Tier: ${m.tier} • can_book=false`,
        tone: "warning",
        tag: "Check",
        onClick: () => nav(`/admin/members/${m.id}`),
      });
    }

    return items.slice(0, 12);
  }, [requestedWithConflict, vehicles, memberships, membershipById, vehicleById, nav]);

  // -------- dashboard search (members + vehicles + bookings) --------
  const searchResults = useMemo(() => {
    const query = normalize(q);
    if (!query || query.length < 2) return [];

    const results = [];

    // memberships / members
    for (const m of memberships) {
      const c = m.crm_contacts || {};
      const hay = normalize(`${c.first_name} ${c.last_name} ${c.email} ${c.phone} ${m.tier} ${m.status}`);
      if (hay.includes(query)) {
        results.push({
          type: "member",
          id: m.id,
          label: c ? fullNameFromContact(c) : "Member",
          sub: `${c.email || "—"} • ${c.phone || "—"} • ${m.tier} • ${m.status}`,
          pill: "Member",
          onPick: () => nav(`/admin/members/${m.id}`),
        });
        if (results.length >= 8) return results;
      }
    }

    // vehicles
    for (const v of vehicles) {
      const hay = normalize(`${v.name} ${v.category} ${v.make} ${v.model} ${v.sub_category} ${v.location}`);
      if (hay.includes(query)) {
        results.push({
          type: "vehicle",
          id: v.id,
          label: v.name,
          sub: `${v.category}${v.sub_category ? ` • ${v.sub_category}` : ""} • ${v.status} • ${v.location || "—"}`,
          pill: "Vehicle",
          onPick: () => nav(`/admin/vehicles/${v.id}`),
        });
        if (results.length >= 10) return results;
      }
    }

    // bookings
    for (const b of bookings) {
      const v = vehicleById.get(b.vehicle_id);
      const m = membershipById.get(b.membership_id);
      const c = m?.crm_contacts || null;
      const hay = normalize(
        `${b.id} ${b.status} ${b.start_date} ${b.end_date} ${b.concierge} ${v?.name || ""} ${c ? fullNameFromContact(c) : ""}`
      );
      if (hay.includes(query)) {
        results.push({
          type: "booking",
          id: b.id,
          label: `Booking ${String(b.id).slice(0, 8)}`,
          sub: `${b.start_date} → ${b.end_date} • ${v?.name || "Vehicle"} • ${c ? fullNameFromContact(c) : "Member"}`,
          pill: "Booking",
          onPick: () => nav(`/admin/bookings`),
        });
        if (results.length >= 12) return results;
      }
    }

    return results;
  }, [q, memberships, vehicles, bookings, nav, vehicleById, membershipById]);

  function onPickSearch(item) {
    setSearchOpen(false);
    setQ("");
    item.onPick?.();
  }

  const bookingsForBars = bookings; // for StatusBars

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <h1 className="page__title">Dashboard</h1>
          <div className="page__sub">
            Ops overview (live from Supabase) • Today: <b>{today}</b>
          </div>
        </div>

        <div className="dashActions">
          <Link className="btn btn--primary" to="/admin/bookings/new">
            + New Booking
          </Link>
          <Link className="btn btn--ghost" to="/admin/members/new">
            + Add Member
          </Link>
          <Link className="btn btn--ghost" to="/admin/vehicles/new">
            + Add Vehicle
          </Link>
        </div>
      </div>

      {/* Dashboard search (page-local) */}
      <div className="dashSearch">
        <div className="dashSearch__label">Quick search</div>
        <div className="dashSearch__wrap">
          <input
            className="dashSearch__input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search members, vehicles, bookings…"
            disabled={loading}
          />

          {searchOpen && q.trim().length >= 2 && (
            <div className="dashSearch__menu">
              {searchResults.map((r) => (
                <button
                  key={`${r.type}_${r.id}`}
                  type="button"
                  className="dashSearch__item"
                  onClick={() => onPickSearch(r)}
                >
                  <div className="dashSearch__itemTop">
                    <span className="dashSearch__pill">{r.pill}</span>
                    <span className="dashSearch__labelText">{r.label}</span>
                  </div>
                  <div className="dashSearch__sub">{r.sub}</div>
                </button>
              ))}
              {!searchResults.length && <div className="dashSearch__empty">No matches.</div>}
              <div className="dashSearch__hint">Tip: type at least 2 characters.</div>
            </div>
          )}
        </div>
      </div>

      {loading && <div className="memMuted">Loading dashboard…</div>}
      {err && <div className="adminAuth__error" style={{ marginTop: 10 }}>{err}</div>}

      {/* KPIs */}
      <div className="grid grid--4">
        <Kpi title="Active members" value={stats.activeMembers} />
        <Kpi title="Prospects" value={stats.prospects} />
        <Kpi title="Vehicles available" value={stats.vehiclesAvailable} />
        <Kpi title="Vehicles ops hold" value={stats.vehiclesOpsHold} />
      </div>

      <div className="grid grid--4" style={{ marginTop: 12 }}>
        <Kpi title="Requested bookings" value={stats.requested} />
        <Kpi title="Approved bookings" value={stats.approved} />
        <Kpi title="Active trips" value={stats.active} />
        <Kpi title="Conflicts" value={conflictsCount} />
      </div>

      {/* Work queue + status snapshot */}
      <div className="grid grid--2" style={{ marginTop: 14 }}>
        <Card
          title="Work queue"
          subtitle="What needs attention"
          right={<div className="muted">Conflicts: {conflictsCount}</div>}
        >
          <div className="stack">
            {workQueue.map((a) => (
              <button
                key={a.key}
                type="button"
                className="queueItem"
                onClick={a.onClick}
                style={{ width: "100%", textAlign: "left" }}
              >
                <div className="queueItem__main">
                  <div className="queueItem__label">{a.label}</div>
                  <div className="queueItem__sub">{a.sub}</div>
                </div>
                <Badge tone={a.tone}>{a.tag}</Badge>
              </button>
            ))}
            {workQueue.length === 0 && <div className="muted">All clear.</div>}
          </div>
        </Card>

        <Card title="Bookings snapshot" subtitle="Counts by status (DB)">
          <StatusBars bookings={bookingsForBars} />
        </Card>
      </div>

      {/* Today: pickups / dropoffs */}
      <div className="grid grid--2" style={{ marginTop: 14 }}>
        <Card
          title="Today’s pickups"
          subtitle={`Approved/active bookings starting today • ${today}`}
          right={<Link className="link" to="/admin/bookings">View bookings</Link>}
        >
          <BookingMiniList rows={todaysPickups} onVehicle={(id) => nav(`/admin/vehicles/${id}`)} onMember={(id) => nav(`/admin/members/${id}`)} />
        </Card>

        <Card
          title="Today’s drop-offs"
          subtitle={`Approved/active bookings ending today • ${today}`}
          right={<Link className="link" to="/admin/bookings">View bookings</Link>}
        >
          <BookingMiniList rows={todaysDropoffs} onVehicle={(id) => nav(`/admin/vehicles/${id}`)} onMember={(id) => nav(`/admin/members/${id}`)} />
        </Card>
      </div>

      {/* Next 7 days + low days */}
      <div className="grid grid--2" style={{ marginTop: 14 }}>
        <Card
          title="Vehicles going out soon"
          subtitle={`Approved bookings starting in next 7 days • ${today} → ${next7}`}
          right={<Link className="link" to="/admin/bookings">View bookings</Link>}
        >
          <BookingMiniList rows={goingOutSoon} onVehicle={(id) => nav(`/admin/vehicles/${id}`)} onMember={(id) => nav(`/admin/members/${id}`)} />
        </Card>

        <Card
          title="Members low on days"
          subtitle="Active members with days_remaining ≤ 2"
          right={<Link className="link" to="/admin/members">View members</Link>}
        >
          <div className="stack">
            {lowDays.length ? (
              lowDays.map((m) => (
                <div key={m.id} className="dashMiniRow">
                  <div className="dashMiniRow__main">
                    <div className="dashMiniRow__top">
                      <button className="linkBtn" type="button" onClick={() => nav(`/admin/members/${m.id}`)}>
                        {m.name}
                      </button>
                      <span className="muted"> • {m.tier}</span>
                    </div>
                    <div className="dashMiniRow__sub">{m.email} • {m.phone}</div>
                  </div>
                  <div className="dashMiniRow__right">
                    <Badge tone={m.days <= 0 ? "danger" : "warning"}>{m.days} days</Badge>
                    <Badge tone={m.can_book ? "success" : "warning"}>{m.can_book ? "Ready" : "Blocked"}</Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="muted">No low-days members right now.</div>
            )}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Quick links" subtitle="Common concierge actions">
          <div className="dashQuickLinks">
            <Link className="btn btn--primary" to="/admin/bookings">Bookings</Link>
            <Link className="btn btn--ghost" to="/admin/members">Members</Link>
            <Link className="btn btn--ghost" to="/admin/vehicles">Vehicles</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

// -------- small components --------
function Kpi({ title, value }) {
  return (
    <div className="kpi">
      <div className="kpi__label">{title}</div>
      <div className="kpi__value">{value}</div>
    </div>
  );
}

function StatusBars({ bookings }) {
  const counts = useMemo(() => {
    const base = { requested: 0, approved: 0, active: 0, completed: 0, cancelled: 0 };
    for (const b of bookings || []) {
      const k = normalize(b.status);
      if (base[k] !== undefined) base[k] += 1;
    }
    return base;
  }, [bookings]);

  const max = Math.max(...Object.values(counts), 1);

  const rows = [
    ["Requested", counts.requested],
    ["Approved", counts.approved],
    ["Active", counts.active],
    ["Completed", counts.completed],
    ["Cancelled", counts.cancelled],
  ];

  return (
    <div className="bars">
      {rows.map(([k, v]) => (
        <div key={k} className="bar">
          <div className="bar__top">
            <span className="bar__label">{k}</span>
            <span className="bar__value">{v}</span>
          </div>
          <div className="bar__track">
            <div className="bar__fill" style={{ width: `${(v / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function enrichBooking(b, membershipById, vehicleById) {
  const m = membershipById.get(b.membership_id) || null;
  const c = m?.crm_contacts || null;
  const v = vehicleById.get(b.vehicle_id) || null;

  return {
    ...b,
    memberName: c ? fullNameFromContact(c) : String(b.membership_id || "").slice(0, 8),
    memberId: m?.id || b.membership_id,
    vehicleName: v ? v.name : String(b.vehicle_id || "").slice(0, 8),
    vehicleId: b.vehicle_id,
  };
}

function BookingMiniList({ rows, onVehicle, onMember }) {
  return (
    <div className="stack">
      {rows.length ? (
        rows.map((b) => (
          <div key={b.id} className="dashMiniRow">
            <div className="dashMiniRow__main">
              <div className="dashMiniRow__top">
                <span className="dashMiniRow__dates">{b.start_date} → {b.end_date}</span>
                <span className="muted"> • </span>
                <button className="linkBtn" type="button" onClick={() => onVehicle?.(b.vehicleId)}>
                  {b.vehicleName}
                </button>
              </div>
              <div className="dashMiniRow__sub">
                Member:{" "}
                <button className="linkBtn" type="button" onClick={() => onMember?.(b.memberId)}>
                  {b.memberName}
                </button>
                {" "}• Concierge: <span className="muted">{b.concierge || "—"}</span>
              </div>

              {(b.pickup_location || b.dropoff_location) && (
                <div className="dashMiniRow__sub">
                  <span className="muted">Pickup:</span> {b.pickup_location || "—"}
                  <span className="muted"> • Drop-off:</span> {b.dropoff_location || "—"}
                </div>
              )}

              {b.notes ? <div className="dashMiniRow__note">{b.notes}</div> : null}
            </div>

            <div className="dashMiniRow__right">
              <Badge tone={toneForBookingStatus(b.status)}>{normalize(b.status)}</Badge>
            </div>
          </div>
        ))
      ) : (
        <div className="muted">Nothing scheduled.</div>
      )}
    </div>
  );
}